import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Check } from 'react-feather';
import Checkbox, { CheckboxSizes, CheckboxTypes } from '../checkbox';
import Image from '../image';
import Icon, { IconColors, IconSizes } from '../icon';
import './list-item.scss';

const ListItemSeparatorId = '_line_';

const SelectionStyles = {
  CHECKBOX: 'checkbox',
  TICK: 'tick',
};

/* eslint-disable jsx-a11y/mouse-events-have-key-events */
const ListItem = React.forwardRef(({
  data,
  renderIcon, onClick, selectionStyle,
  renderCustomView,
  onItemHighlight,
}, ref) => {
  const {
    highlighted,
    id, label, shortcut, helper, disabled, selected, showDivider, image,
  } = data;
  const elTabIndex = disabled ? -1 : 0;

  const getCheckBox = () => (
    <Checkbox
      disabled={disabled}
      type={CheckboxTypes.CHECKBOX}
      size={CheckboxSizes.SMALL}
      checked={selected}
    />
  );

  const getImageOrIcon = () => {
    if (image) {
      return <Image className="list-item-image" src={image} />;
    }
    if (renderIcon && typeof renderIcon === 'function') {
      return (
        <span className="list-item-icon">
          {renderIcon(data)}
        </span>
      );
    }
    return null;
  };

  const getSelectTick = () => {
    if (selected) {
      return <Icon className="list-item-tick" icon={Check} color={IconColors.PRIMARY} size={IconSizes.SMALL} />;
    }

    return null;
  };

  const getClassNames = () => clsx('list-item-cont', {
    disabled,
    selected,
    'ddi-focus': highlighted,
    'item-top-border': showDivider,
  });

  if (id === ListItemSeparatorId) {
    return <hr className="list-item-hr" />;
  }

  const handleClick = (event) => {
    if (disabled) return;
    // eslint-disable-next-line no-param-reassign,no-underscore-dangle
    if (onClick && typeof onClick === 'function') {
      onClick(id, !selected, {
        ...data,
      }, event);
    }
  };

  const renderView = () => {
    if (renderCustomView && typeof renderCustomView === 'function') {
      return renderCustomView(data);
    }
    return (
      <>
        {getImageOrIcon()}
        {label && <span className="list-item-label">{label}</span>}
        {helper && <span className="list-item-helper">{helper}</span>}
        {shortcut && <span className="list-item-shortcut">{shortcut}</span>}
      </>
    );
  };

  const handleHighlight = () => {
    onItemHighlight(id);
  };

  return (
    <div
      className={getClassNames()}
      onClick={handleClick}
      onMouseOver={handleHighlight}
      ref={ref}
      tabIndex={elTabIndex}
      onFocus={handleHighlight}
    >
      {selectionStyle === SelectionStyles.CHECKBOX && getCheckBox()}
      {renderView()}
      {selectionStyle === SelectionStyles.TICK && getSelectTick()}
    </div>
  );
});

const ListItemDataPropTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  shortcut: PropTypes.string,
  helper: PropTypes.string,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  showDivider: PropTypes.bool,
  highlighted: PropTypes.bool,
  image: PropTypes.string,
};

const ListItemPropTypes = {
  renderIcon: PropTypes.func,
  onClick: PropTypes.func,
  onIconButtonClick: PropTypes.func,
  renderCustomView: PropTypes.oneOf([PropTypes.func, null]),
  onItemHighlight: PropTypes.func,
  selectionStyle: PropTypes.oneOf(Object.values(SelectionStyles)),
};

ListItem.propTypes = {
  data: PropTypes.shape(ListItemDataPropTypes),
  renderIcon: PropTypes.func,
  onClick: PropTypes.func,
  onIconButtonClick: PropTypes.func,
  renderCustomView: PropTypes.oneOf([PropTypes.func, null]),
  onItemHighlight: PropTypes.func,
  selectionStyle: PropTypes.oneOf(Object.values(SelectionStyles)),
};

ListItem.defaultProps = {
  data: {},
  renderIcon: null,
  onClick: () => {},
  onIconButtonClick: () => {},
  renderCustomView: null,
  onItemHighlight: () => {},
  selectionStyle: SelectionStyles.TICK,
};

export {
  ListItem as default,
  ListItemPropTypes,
  ListItemDataPropTypes,
  SelectionStyles,
};
